import type { SimilarItemAlert, SimilarItemAlertsSort } from './similarItemAlerts.types';

import { ApiHosts, handleResponseWithNon200Errors, makeDelete, makeGet, makePatch, makePost } from '../../api/helpers';

type CheckSimilarItemAlertRequest = {
    authToken: string;
    deployment: string;
    lotId: number;
};

type CheckSimilarItemAlertResponse = {
    payload: { savedItemId: number };
};

/**
 * Looks up whether the authenticated bidder has a Similar Item Alert for a lot.
 * @function getSimilarItemAlert
 * @category Saved Item Alerts API
 * @see https://savedsearch-api-DEPLOYMENT.liveauctioneers.com/saved-items/check?lotId=
 */
export const getSimilarItemAlert = ({ authToken, deployment, lotId }: CheckSimilarItemAlertRequest) =>
    new Promise<CheckSimilarItemAlertResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: `saved-items/check?lotId=${lotId}`,
            authToken,
            deployment,
            path: ApiHosts.SavedItemAlerts,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

type PostSimilarItemAlertRequest = {
    authToken: string;
    deployment: string;
    lotId: number;
    lotTitle: string;
};

type PostSimilarItemAlertResponse = {
    payload: { savedItemId: number };
};

/**
 * Creates a Similar Item Alert for a lot.
 * `lotTitle` is persisted verbatim and never backfilled by the backend — omitting it stores an
 * empty string, which silently breaks the title sorts on the alert management lists.
 * @function postSimilarItemAlert
 * @category Saved Item Alerts API
 * @see https://savedsearch-api-DEPLOYMENT.liveauctioneers.com/saved-items
 */
export const postSimilarItemAlert = ({ authToken, deployment, lotId, lotTitle }: PostSimilarItemAlertRequest) =>
    new Promise<PostSimilarItemAlertResponse>((resolve, reject) => {
        const request = makePost({
            apiPath: 'saved-items',
            authToken,
            deployment,
            path: ApiHosts.SavedItemAlerts,
        });
        request.send({ lotId, lotTitle, marketplace: 'LA', sendAlert: true });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

type GetSimilarItemAlertsRequest = {
    authToken: string;
    deployment: string;
    keyword?: string;
    page: number;
    pageSize: number;
    sort: SimilarItemAlertsSort;
};

type GetSimilarItemAlertsResponse = {
    payload: {
        data: SimilarItemAlert[];
        page: number;
        pageSize: number;
        totalRecords: number;
    };
};

/**
 * Lists the authenticated bidder's Similar Item Alerts. Returns the raw savedsearch-api rows (no lot
 * data) plus pagination — the caller hydrates each `lotId` into a card via item-summaries.
 * A non-empty `keyword` filters case-insensitively on `lotTitle` before pagination, so `totalRecords`
 * reflects the filtered set; an empty or whitespace-only keyword is omitted and behaves as unfiltered.
 * @function getSimilarItemAlerts
 * @category Saved Item Alerts API
 * @see https://savedsearch-api-DEPLOYMENT.liveauctioneers.com/saved-items?page=&pageSize=&sort=&keyword=
 */
export const getSimilarItemAlerts = ({
    authToken,
    deployment,
    keyword,
    page,
    pageSize,
    sort,
}: GetSimilarItemAlertsRequest) =>
    new Promise<GetSimilarItemAlertsResponse>((resolve, reject) => {
        const trimmedKeyword = keyword?.trim() ?? '';
        const keywordParam = trimmedKeyword ? `&keyword=${encodeURIComponent(trimmedKeyword)}` : '';
        const request = makeGet({
            apiPath: `saved-items?page=${page}&pageSize=${pageSize}&sort=${sort}${keywordParam}`,
            authToken,
            deployment,
            path: ApiHosts.SavedItemAlerts,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

type PatchSimilarItemAlertEmailRequest = {
    authToken: string;
    deployment: string;
    savedItemId: number;
    sendAlert: boolean;
};

/**
 * Toggles a single alert's email delivery without removing it.
 * @function patchSimilarItemAlertEmail
 * @category Saved Item Alerts API
 * @see https://savedsearch-api-DEPLOYMENT.liveauctioneers.com/saved-items/{savedItemId}/alert
 */
export const patchSimilarItemAlertEmail = ({
    authToken,
    deployment,
    savedItemId,
    sendAlert,
}: PatchSimilarItemAlertEmailRequest) =>
    new Promise<{ payload: { success: boolean } }>((resolve, reject) => {
        const request = makePatch({
            apiPath: `saved-items/${savedItemId}/alert`,
            authToken,
            deployment,
            path: ApiHosts.SavedItemAlerts,
        });
        request.send({ sendAlert });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

type PatchAllSimilarItemAlertsEmailRequest = {
    authToken: string;
    deployment: string;
    sendAlert: boolean;
};

/**
 * Toggles email delivery for every one of the bidder's alerts at once (section-level checkbox).
 * @function patchAllSimilarItemAlertsEmail
 * @category Saved Item Alerts API
 * @see https://savedsearch-api-DEPLOYMENT.liveauctioneers.com/saved-items/alert
 */
export const patchAllSimilarItemAlertsEmail = ({
    authToken,
    deployment,
    sendAlert,
}: PatchAllSimilarItemAlertsEmailRequest) =>
    new Promise<{ payload: { success: boolean } }>((resolve, reject) => {
        const request = makePatch({
            apiPath: 'saved-items/alert',
            authToken,
            deployment,
            path: ApiHosts.SavedItemAlerts,
        });
        request.send({ sendAlert });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });

type DeleteSimilarItemAlertRequest = {
    authToken: string;
    deployment: string;
    savedItemId: number;
};

/**
 * Deletes a Similar Item Alert.
 * @function deleteSimilarItemAlert
 * @category Saved Item Alerts API
 * @see https://savedsearch-api-DEPLOYMENT.liveauctioneers.com/saved-items/{savedItemId}
 */
export const deleteSimilarItemAlert = ({ authToken, deployment, savedItemId }: DeleteSimilarItemAlertRequest) =>
    new Promise<{ payload: { success: boolean } }>((resolve, reject) => {
        const request = makeDelete({
            apiPath: `saved-items/${savedItemId}`,
            authToken,
            deployment,
            path: ApiHosts.SavedItemAlerts,
        });
        request.end((err, response) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                request,
                resolve,
                response,
            })
        );
    });
